import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import ProjectCard from './ProjectCard';
import LoadingSpinner from '../ui/LoadingSpinner';
import ErrorDisplay from '../ui/ErrorDisplay';
import EmptyState from '../ui/EmptyState';
import InfiniteScrollLoader from '../InfiniteScrollLoader';
import useInfiniteScroll from './useInfiniteScroll';

const ProjectByPlaceComponent = ({ searchKey, idCustomer }) => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const itemsPerPage = 6;
    const dropdownRef = useRef(null);

    // Fetch projects function with pagination
    const fetchProjects = async (page = 1, append = false) => {
        try {
            if (page === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            setError(null);

            const response = await fetch('https://search.mg.formafusion.io/api/search/project-place', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    key: searchKey,
                    idCustomer: idCustomer,
                    page: page,
                    per_page: itemsPerPage,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP Error: ${response.status}`);
            }

            const data = await response.json();

            // Ensure data.data exists and is an array
            const projectsData = Array.isArray(data.data) ? data.data : [];

            if (append) {
                setProjects(prev => {
                    const currentProjects = Array.isArray(prev) ? prev : [];
                    return [...currentProjects, ...projectsData];
                });
            } else {
                setProjects(projectsData);
            }

            // Safely handle pagination data
            if (data.pagination) {
                setCurrentPage(data.pagination.current_page || 1);
                setTotalItems(data.pagination.total || 0);
                setHasMore(data.pagination.current_page < data.pagination.last_page);
            } else {
                setCurrentPage(1);
                setTotalItems(projectsData.length);
                setHasMore(false);
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : 'An error occurred');
            // Ensure projects is always an array even on error
            if (!append) {
                setProjects([]);
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Load more data for infinite scroll
    const loadMoreProjects = useCallback(async () => {
        if (!hasMore || loadingMore) return;
        await fetchProjects(currentPage + 1, true);
    }, [currentPage, hasMore, loadingMore, searchKey, idCustomer]);

    // Infinite scroll hook
    const [isFetching] = useInfiniteScroll(loadMoreProjects, hasMore);

    useEffect(() => {
        if (searchKey && idCustomer) {
            // Reset state when search changes
            setProjects([]);
            setCurrentPage(1);
            setHasMore(true);
            fetchProjects(1, false);
        }
    }, [searchKey, idCustomer]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(null);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [dropdownOpen]);

    // Action handlers
    const handleEdit = (id) => {
        setDropdownOpen(null);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this project?')) {
            try {
                // Add your delete API call here
                // Reset and refetch after deletion
                setProjects([]);
                setCurrentPage(1);
                setHasMore(true);
                await fetchProjects(1, false);
            } catch (error) {
                console.error('Error deleting project:', error);
            }
        }
        setDropdownOpen(null);
    };

    const handleView = (id) => {
        setDropdownOpen(null);
    };

    const handleDuplicate = (id) => {
        setDropdownOpen(null);
    };

    const handleArchive = (id) => {
        setDropdownOpen(null);
    };

    const handleCancel = (id) => {
        setDropdownOpen(null);
    };

    const handlePostpone = (id) => {
        setDropdownOpen(null);
    };

    const handleClose = (id) => {
        setDropdownOpen(null);
    };

    const toggleDropdown = (id) => {
        setDropdownOpen(dropdownOpen === id ? null : id);
    };

    const handleRefresh = () => {
        setProjects([]);
        setCurrentPage(1);
        setHasMore(true);
        fetchProjects(1, false);
    };

    // Loading component for initial load
    if (loading && projects.length === 0) {
        return <LoadingSpinner />;
    }

    // Error component
    if (error && projects.length === 0) {
        return <ErrorDisplay error={error} onRetry={handleRefresh} />;
    }

    // No projects found
    if (!Array.isArray(projects) || (projects.length === 0 && !loading)) {
        return <EmptyState searchKey={searchKey} />;
    }

    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Projets</h1>
                    <p className="mt-1 text-gray-600">
                        {totalItems > 0 ? (
                            <>
                                {Array.isArray(projects) ? projects.length : 0} of {totalItems} projet{totalItems > 1 ? 's' : ''} loaded
                                {hasMore && ' (scroll for more)'}
                            </>
                        ) : (
                            'Aucun projet trouvé'
                        )}
                    </p>
                </div>
                <button
                    onClick={handleRefresh}
                    className="flex items-center px-6 py-3 space-x-2 text-white transition-colors bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700"
                >
                    <RefreshCw className="w-4 h-4" />
                    <span>Refresh</span>
                </button>
            </div>

            {/* Projects Grid */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-2">
                {Array.isArray(projects) && projects.map((project, index) => (
                    <ProjectCard
                        key={`${project.id}-${index}`}
                        project={project}
                        dropdownOpen={dropdownOpen}
                        dropdownRef={dropdownRef}
                        onToggleDropdown={toggleDropdown}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onView={handleView}
                        onDuplicate={handleDuplicate}
                        onArchive={handleArchive}
                        onCancel={handleCancel}
                        onPostpone={handlePostpone}
                        onClose={handleClose}
                    />
                ))}
            </div>

            {/* Infinite Scroll Loader */}
            <InfiniteScrollLoader hasMore={hasMore} loading={loadingMore} />

            {/* Error state for loading more */}
            {error && Array.isArray(projects) && projects.length > 0 && (
                <div className="p-4 text-center rounded-lg bg-red-50">
                    <p className="text-sm text-red-600">Error loading more projects: {error}</p>
                    <button
                        onClick={loadMoreProjects}
                        className="px-4 py-2 mt-2 text-sm text-white bg-red-600 rounded hover:bg-red-700"
                    >
                        Try Again
                    </button>
                </div>
            )}
        </div>
    );
};

export default ProjectByPlaceComponent;